import { inject, injectable } from "inversify";
import { CursorService, DomElementsService, type IService, Logger, type PointerEventPayload, type PointerPosition } from "@designboticteam/core";
import { CursorSnappingService } from "../../input/CursorSnappingService";
import { Workspace3DStoreService } from "../../services/Workspace3DStoreService";
import type { ITool } from "../ITool";
import type { ToolClass } from "./types";

@injectable()
class ToolsManagerService implements IService {
	public readonly serviceName = "ToolsManagerService";
	private readonly logger = new Logger(`🛠️ ${this.serviceName}`, { color: "#ffffff", backgroundColor: "#1d4ed8" });

	private readonly tools = new Map<string, ITool>();

	constructor(
		@inject(DomElementsService) private domElementsService: DomElementsService,
		@inject(CursorService) private cursorService: CursorService,
		@inject(Workspace3DStoreService) private workspace3DStoreService: Workspace3DStoreService,
		@inject(CursorSnappingService) private cursorSnappingService: CursorSnappingService,
	) {
		this.logger.log("constructed");
	}

	/** */
	public initialize(): void {
		this.bindEvents();
		this.logger.log("initialized");
	}

	/** */
	public dispose(): void {
		this.setActiveTool(null);
		this.unbindEvents();

		for (const tool of this.tools.values()) {
			tool.dispose?.();
		}

		this.tools.clear();
		this.logger.log("disposed");
	}

	/** */
	public get activeTool(): ITool | null {
		const toolName = this.workspace3DStoreService.store.getState().tools.activeTool;

		if (toolName === null) {
			return null;
		}

		return this.getToolByNameOrThrow(toolName);
	}

	/** */
	public update(): void {
		this.activeTool?.update?.();
	}

	/** */
	private bindEvents(): void {
		this.domElementsService.onTap.subscribe(this.handleOnTap);
		this.domElementsService.onDoubleTap.subscribe(this.handleDoubleTap);
		this.domElementsService.onPointerDown.subscribe(this.handlePointerDown);
		this.domElementsService.onPointerMove.subscribe(this.handlePointerMove);
		this.domElementsService.onPointerUp.subscribe(this.handlePointerUp);
		this.domElementsService.onDragStart.subscribe(this.handleDragStart);
		this.domElementsService.onDragMove.subscribe(this.handleDragMove);
		this.domElementsService.onDragEnd.subscribe(this.handleDragEnd);
	}

	/** */
	private unbindEvents(): void {
		this.domElementsService.onTap.unsubscribe(this.handleOnTap);
		this.domElementsService.onDoubleTap.unsubscribe(this.handleDoubleTap);
		this.domElementsService.onPointerDown.unsubscribe(this.handlePointerDown);
		this.domElementsService.onPointerMove.unsubscribe(this.handlePointerMove);
		this.domElementsService.onPointerUp.unsubscribe(this.handlePointerUp);
		this.domElementsService.onDragStart.unsubscribe(this.handleDragStart);
		this.domElementsService.onDragMove.unsubscribe(this.handleDragMove);
		this.domElementsService.onDragEnd.unsubscribe(this.handleDragEnd);
	}

	/** */
	public getToolOrThrow<T extends ITool>(Tool: ToolClass<T>): T {
		return this.getToolByNameOrThrow(Tool.toolName) as T;
	}

	private getToolByNameOrThrow(toolName: string): ITool {
		const tool = this.tools.get(toolName);

		if (tool === undefined) {
			throw new Error(`Tool "${toolName}" is not registered`);
		}

		return tool;
	}

	/** */
	public registerTool(tool: ITool): void {
		if (this.tools.has(tool.toolName)) {
			throw new Error(`Tool "${tool.toolName}" is already registered`);
		}

		this.tools.set(tool.toolName, tool);
	}

	/** */
	public setActiveTool = (Tool: ToolClass | null): void => {
		const requestedTool: ITool | null = Tool === null ? null : this.getToolOrThrow(Tool);

		if (this.activeTool === requestedTool) {
			return;
		}

		this.activeTool?.onDeactivate();
		this.workspace3DStoreService.store.setState((state) => ({ tools: { ...state.tools, activeTool: Tool?.toolName ?? null } }));
		this.cursorSnappingService.setContextActive(requestedTool?.snapsWhileActive === true);

		if (requestedTool === null) {
			this.cursorService.hide();
			return;
		}

		this.cursorService.show();
		requestedTool.onActivate();
	};

	private handleOnTap = (pointerPosition: PointerPosition) => {
		this.activeTool?.onTap?.(pointerPosition);
	};

	private handleDoubleTap = (pointerPosition: PointerPosition) => {
		this.activeTool?.onDoubleTap?.(pointerPosition);
	};

	private handlePointerDown = (pointerPosition: PointerPosition) => {
		this.activeTool?.onPointerDown?.(pointerPosition);
	};

	private handlePointerMove = ({ relativePointerPosition }: PointerEventPayload) => {
		this.activeTool?.onPointerMove?.(relativePointerPosition);
	};

	private handlePointerUp = (pointerPosition: PointerPosition) => {
		this.activeTool?.onPointerUp?.(pointerPosition);
	};

	private handleDragStart = (pointerPosition: PointerPosition) => {
		this.activeTool?.onDragStart?.(pointerPosition);
	};

	private handleDragMove = (pointerPosition: PointerPosition) => {
		this.activeTool?.onDragMove?.(pointerPosition);
	};

	private handleDragEnd = (pointerPosition: PointerPosition) => {
		this.activeTool?.onDragEnd?.(pointerPosition);
	};
}

export { ToolsManagerService };
